"use client";

import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";

import { cn } from "~/shared/lib/utils";
import { useAppPortalContainer } from "~/shared/lib/portal-context";

function Popover({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Root>) {
    return <PopoverPrimitive.Root data-slot="popover" {...props} />;
}

function PopoverTrigger({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Trigger>) {
    return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />;
}

function PopoverContent({
    className,
    align = "center",
    sideOffset = 4,
    onCloseAutoFocus,
    /**
     * Рендерить контент inline (без Portal). Нужно внутри модального `Dialog`: его портал в
     * `app-root` попадает под overlay и `pointer-events:none`, поэтому popover виден, но не
     * кликается (например `DatePicker` в настройках папки). Inline-контент живёт внутри
     * `DialogContent`, где z-index и pointer-events уже корректны.
     */
    disablePortal = false,
    ...props
}: React.ComponentProps<typeof PopoverPrimitive.Content> & { disablePortal?: boolean }) {
    const container = useAppPortalContainer();
    const content = (
        <PopoverPrimitive.Content
            data-slot="popover-content"
            align={align}
            sideOffset={sideOffset}
            onCloseAutoFocus={onCloseAutoFocus ?? ((event) => event.preventDefault())}
            className={cn(
                "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-72 origin-(--radix-popover-content-transform-origin) rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-hidden",
                className,
            )}
            {...props}
        />
    );
    if (disablePortal) return content;
    return <PopoverPrimitive.Portal container={container}>{content}</PopoverPrimitive.Portal>;
}

function PopoverAnchor({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Anchor>) {
    return <PopoverPrimitive.Anchor data-slot="popover-anchor" {...props} />;
}

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor };
